/**
 * Settings - Templates Page
 * 
 * Manage invoice templates with visual builder
 */

import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, MoreVertical, Edit, Copy, Trash2, Star, Eye } from 'lucide-react';
import { toast } from 'sonner';
import type { InvoiceTemplate } from '@/lib/invoice-template-types';
import { createDefaultTemplate } from '@/lib/invoice-template-types';
import {
  mockPreviewData,
  duplicateTemplate,
} from '@/lib/mock-invoice-templates';
import { useInvoiceTemplates } from '@/contexts/InvoiceTemplateContext';
import { InvoiceTemplateBuilder } from '@/components/billing/InvoiceTemplateBuilder';
import type { InvoiceTemplateBuilderHandle } from '@/components/billing/InvoiceTemplateBuilder';
import { InvoicePreview } from '@/components/billing/InvoicePreview';

export default function SettingsTemplates() {
  const { templates, addTemplate, updateTemplate, deleteTemplate, setDefault } = useInvoiceTemplates();
  const [builderOpen, setBuilderOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<InvoiceTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<InvoiceTemplate | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<InvoiceTemplate | null>(null);
  const [editedTemplateData, setEditedTemplateData] = useState<Partial<InvoiceTemplate> | null>(null);
  const builderRef = useRef<InvoiceTemplateBuilderHandle>(null);
  const dropdownActionRef = useRef(false);

  // Create new template
  const handleCreateTemplate = () => {
    setSelectedTemplate(createDefaultTemplate('New Template'));
    setBuilderOpen(true);
  };

  // Edit template
  const handleEditTemplate = (template: InvoiceTemplate) => {
    setSelectedTemplate(template);
    setBuilderOpen(true);
  };

  // Preview template
  const handlePreviewTemplate = (template: InvoiceTemplate) => {
    setPreviewTemplate(template);
    setPreviewOpen(true);
  };

  // Duplicate template
  const handleDuplicateTemplate = (template: InvoiceTemplate) => {
    const newTemplate = duplicateTemplate(template, `${template.name} (Copy)`);
    addTemplate(newTemplate);
    toast.success(`"${template.name}" has been duplicated.`);
  };

  // Delete template
  const handleDeleteTemplate = (template: InvoiceTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (templateToDelete) {
      deleteTemplate(templateToDelete.id);
      toast.success(`"${templateToDelete.name}" has been deleted.`);
      setTemplateToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  // Set as default
  const handleSetDefault = (template: InvoiceTemplate) => {
    setDefault(template.id);
    toast.success(`"${template.name}" is now the default template.`);
  };

  // Track builder changes
  const handleBuilderChange = useCallback((data: Partial<InvoiceTemplate>) => {
    setEditedTemplateData(data);
    // Sync live builder edits into the shared context so the wizard and
    // any other consumer always reads the latest column/layout configuration
    // — even before the user explicitly clicks Save.
    if (data.id !== undefined) {
      updateTemplate(data.id, data);
    }
  }, [updateTemplate]);

  // Save template — merges builder edits with original template
  const handleSaveTemplate = (templateOrPartial: InvoiceTemplate | Partial<InvoiceTemplate>) => {
    if (!selectedTemplate) return;
    // Merge the partial data with the original template
    const merged: InvoiceTemplate = {
      ...selectedTemplate,
      ...templateOrPartial,
      updatedAt: new Date(),
    };
    const existingIndex = templates.findIndex((t) => t.id === merged.id);
    if (existingIndex >= 0) {
      // Update existing — use context method which calls the API
      updateTemplate(merged.id, merged);
      toast.success(`"${merged.name}" has been updated.`);
    } else {
      // Add new — use context method which calls the API
      addTemplate(merged);
      toast.success(`"${merged.name}" has been created.`);
    }
    setBuilderOpen(false);
    setSelectedTemplate(null);
    setEditedTemplateData(null);
  };

  // Header save button — uses tracked builder changes
  const handleHeaderSave = () => {
    if (editedTemplateData) {
      handleSaveTemplate(editedTemplateData);
    } else if (selectedTemplate) {
      handleSaveTemplate(selectedTemplate);
    }
  };

  // Render builder within the page layout (not full-screen)
  // Use negative margins to break out of the app-layout p-6 wrapper and use viewport height
  if (builderOpen && selectedTemplate) {
    return (
      <div className="-m-6 flex flex-col" style={{ height: 'calc(100vh - 3.5rem)' }}>
        {/* Header */}
        <div className="border-b bg-background px-6 py-4 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                {selectedTemplate.id ? 'Edit Template' : 'Create Template'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Configure your invoice template settings and preview in real-time
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setBuilderOpen(false);
                  setSelectedTemplate(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleHeaderSave}>
                Save Template
              </Button>
            </div>
          </div>
        </div>

        {/* Builder Content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <InvoiceTemplateBuilder
            ref={builderRef}
            template={selectedTemplate}
            onSave={handleSaveTemplate}
            onChange={handleBuilderChange}
            onCancel={() => {
              setBuilderOpen(false);
              setSelectedTemplate(null);
              setEditedTemplateData(null);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-16 items-center justify-between px-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Invoice Templates</h1>
            <p className="text-sm text-muted-foreground">
              Design professional invoices with our visual builder
            </p>
          </div>
          <Button onClick={handleCreateTemplate} size="lg" className="shadow-sm">
            <Plus className="h-4 w-4 mr-2" />
            Create Template
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="container max-w-7xl py-8 px-6">
          <Tabs defaultValue="invoice-templates" className="space-y-8">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="invoice-templates" className="data-[state=active]:bg-background">
                Invoice Templates
              </TabsTrigger>
              <TabsTrigger value="email-templates" className="data-[state=active]:bg-background">
                Email Templates
              </TabsTrigger>
            </TabsList>

            {/* Invoice Templates Tab */}
            <TabsContent value="invoice-templates" className="space-y-6 mt-0">
              {templates.length > 0 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {templates.length} template{templates.length !== 1 ? 's' : ''}
                  </p>
                </div>
              )}

              {templates.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-16">
                    <div className="rounded-full bg-muted p-4 mb-4">
                      <Plus className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">No templates yet</h3>
                    <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
                      Create your first invoice template to get started. Choose from our library or build your own from scratch.
                    </p>
                    <div className="flex gap-3">
                      <Button onClick={handleCreateTemplate} size="lg">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Template
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {templates.map((template) => (
                    <Card 
                      key={template.id} 
                      className="group hover:shadow-lg transition-all duration-200 hover:border-primary/50 bg-card cursor-pointer"
                      onClick={() => {
                        // Skip if a dropdown menu action just fired (e.g. Preview)
                        if (dropdownActionRef.current) {
                          dropdownActionRef.current = false;
                          return;
                        }
                        handleEditTemplate(template);
                      }}
                    >
                      <CardHeader className="pb-4 space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <CardTitle className="text-lg font-semibold">{template.name}</CardTitle>
                              {template.isDefault && (
                                <Badge variant="default" className="shrink-0">
                                  <Star className="h-3 w-3 mr-1 fill-current" />
                                  Default
                                </Badge>
                              )}
                            </div>
                            <CardDescription className="text-sm leading-relaxed">
                              {template.description || 'No description provided'}
                            </CardDescription>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => { dropdownActionRef.current = true; handleEditTemplate(template); }}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Template
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { dropdownActionRef.current = true; handlePreviewTemplate(template); }}>
                                <Eye className="h-4 w-4 mr-2" />
                                Preview
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { dropdownActionRef.current = true; handleDuplicateTemplate(template); }}>
                                <Copy className="h-4 w-4 mr-2" />
                                Duplicate
                              </DropdownMenuItem>
                              {!template.isDefault && (
                                <DropdownMenuItem onClick={() => { dropdownActionRef.current = true; handleSetDefault(template); }}>
                                  <Star className="h-4 w-4 mr-2" />
                                  Set as Default
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => { dropdownActionRef.current = true; handleDeleteTemplate(template); }}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="secondary" className="text-xs">
                            {template.sections.length} sections
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {template.settings.groupBy === 'none'
                              ? 'No grouping'
                              : `By ${template.settings.groupBy}`}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t">
                          <span className="font-medium">Last updated</span>
                          <span>{new Date(template.updatedAt).toLocaleDateString()}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Email Templates Tab */}
            <TabsContent value="email-templates" className="space-y-6 mt-0">
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <div className="rounded-full bg-muted p-4 mb-4">
                    <Plus className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Coming Soon</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-sm">
                    Email template customization will be available soon. Stay tuned for updates!
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Sheet */}
      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent side="right" className="w-full sm:max-w-4xl p-0 overflow-y-auto">
          <SheetHeader className="px-6 pt-6 pb-4 border-b">
            <SheetTitle>Invoice Template Preview</SheetTitle>
            <SheetDescription>
              Preview how this template will appear on generated invoices
            </SheetDescription>
          </SheetHeader>
          <div className="p-6">
            {previewTemplate && (
              <InvoicePreview template={previewTemplate} previewData={mockPreviewData} />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
